import { useEffect, useState } from "react";
import { BASE_URL } from "../apiConfig";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const InsightBlock = ({
  description,
  summary,
}: {
  description?: string;
  summary?: string;
}) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
    <p className="text-sm text-slate-600">{description}</p>
    {summary ? (
      <div className="mt-3 border-l-4 border-slate-400 pl-3">
        <p className="text-xs font-medium text-slate-700">Key insight</p>
        <p className="text-sm text-slate-700">{summary}</p>
      </div>
    ) : null}
  </div>
);

const Card = ({
  title,
  subtitle,
  children,
  headerRight,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  headerRight?: React.ReactNode;
}) => (
  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
      <div>
        <h3 className="font-semibold text-slate-900">{title}</h3>
        {subtitle ? (
          <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>
        ) : null}
      </div>
      {headerRight}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

type StoreInsightData = {
  description?: string;
  summary?: string;
  data?: any;
  confidence?: string;
  error?: string;
};

export default function StoreTab() {
  const [conversionRates, setConversionRates] = useState<StoreInsightData | null>(null);
  const [cityPerformance, setCityPerformance] = useState<StoreInsightData | null>(null);
  const [topBottomPerformers, setTopBottomPerformers] = useState<StoreInsightData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchStoreInsights() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${BASE_URL}/store-insights`);
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const data = await res.json();
        setConversionRates(data.conversion_rates);
        setCityPerformance(data.city_performance);
        setTopBottomPerformers(data.top_bottom_performers);
      } catch (err: any) {
        setError(err.message || "Unknown error");
      } finally {
        setLoading(false);
      }
    }
    fetchStoreInsights();
  }, []);

  // Format data for recharts
  const formatConversionRates = (data: any) =>
    Array.isArray(data?.data)
      ? data.data.map((item: any) => ({
          store: item.store_id,
          rate: +(item.conversion_rate * 100).toFixed(2),
        }))
      : [];

  const formatCityPerformance = (data: any) =>
    Array.isArray(data?.data)
      ? data.data.map((item: any) => ({
          city: item.city,
          sales: Math.round(item.sales_value / 100000) / 10, // millions
        }))
      : [];

  const formatTopBottom = (data: any, key: "top_stores" | "bottom_stores") =>
    Array.isArray(data?.data?.[key])
      ? data.data[key].map((item: any) => ({
          store: item.store_id,
          rate: +(item.conversion_rate * 100).toFixed(2),
        }))
      : [];

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="h-6 w-56 animate-pulse rounded bg-slate-200" />
        <div className="mt-6 h-64 w-full animate-pulse rounded-2xl bg-slate-200" />
      </div>
    );
  }
  if (error) return <div className="p-6 md:p-8 text-red-600">Error: {error}</div>;

  const EmptyState = ({ label }: { label: string }) => (
    <div className="flex h-[260px] items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-500">
      {label}
    </div>
  );

  // Add these colors for consistent styling
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  return (
    <div className="p-6 md:p-8 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900">Store Insights</h1>
      </div>

      {/* Conversion Rates */}
      <Card title="Store Conversion Rates" subtitle="Rate = Orders / Footfall">
        <div className="mb-5">
          <InsightBlock
            description={conversionRates?.description}
            summary={conversionRates?.summary}
          />
        </div>

        <div className="h-[320px] w-full">
          {formatConversionRates(conversionRates).length === 0 ? (
            <EmptyState label="No conversion data available." />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={formatConversionRates(conversionRates)}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <CartesianGrid stroke="#F3F4F6" strokeDasharray="3 3" />
                <XAxis dataKey="store" tick={{ fill: "#6B7280", fontSize: 12 }} />
                <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} unit="%" />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="rate" 
                  name="Conversion Rate (%)" 
                  stroke="#8884d8" 
                  strokeWidth={2}
                  dot={{ fill: "#8884d8" }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>

      {/* Two-column: City Performance / Top-Bottom */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card title="City Performance" subtitle="Sales by city (₹ in millions)">
          <div className="mb-5">
            <InsightBlock
              description={cityPerformance?.description}
              summary={cityPerformance?.summary}
            />
          </div>

          <div className="h-[320px] w-full">
            {formatCityPerformance(cityPerformance).length === 0 ? (
              <EmptyState label="No city performance data." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={formatCityPerformance(cityPerformance)}
                  margin={{ top: 10, right: 10, left: 0, bottom: 80 }} // Increased bottom margin
                  barCategoryGap="25%"
                >
                  <CartesianGrid stroke="#F3F4F6" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="city"
                    tick={{ 
                      fill: "#6B7280", 
                      fontSize: 12 
                    }}
                    angle={-45}  // Adjusted angle for better readability
                    textAnchor="end"
                    interval={0}
                    height={60}  // Increased height for x-axis
                    dy={8}       // Adjust vertical position of labels
                  />
                  <YAxis
                    tick={{ fill: "#6B7280", fontSize: 12 }}
                    tickFormatter={(v) => `₹${v}M`}
                  />
                  <Tooltip formatter={(v: any) => [`₹${v}M`, "Sales"]} />
                  {/* <Legend /> */}
                  <Bar dataKey="sales" name="" fill="#9CA3AF" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <div className="space-y-6">
          <Card title="Top Performing Stores" subtitle="Top 10 by conversion rate">
            <div className="h-[320px] w-full">
              {formatTopBottom(topBottomPerformers, "top_stores").length === 0 ? (
                <EmptyState label="No top stores data." />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={formatTopBottom(topBottomPerformers, "top_stores")}
                      dataKey="rate"
                      nameKey="store"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label
                    >
                      {formatTopBottom(topBottomPerformers, "top_stores").map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: any) => [`${v}%`, "Conversion Rate"]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>

          <Card title="Bottom Performing Stores" subtitle="Bottom 10 by conversion rate">
            <div className="h-[320px] w-full">
              {formatTopBottom(topBottomPerformers, "bottom_stores").length === 0 ? (
                <EmptyState label="No bottom stores data." />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={formatTopBottom(topBottomPerformers, "bottom_stores")}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                    barCategoryGap="30%"
                  >
                    <defs>
                      <linearGradient id="colorRate" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#FF8042" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#FF8042" stopOpacity={0.3}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#F3F4F6" strokeDasharray="3 3" />
                    <XAxis dataKey="store" tick={{ fill: "#6B7280", fontSize: 12 }} />
                    <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} unit="%" />
                    <Tooltip />
                    <Legend />
                    <Bar 
                      dataKey="rate" 
                      name="Conversion Rate (%)" 
                      fill="url(#colorRate)" 
                      radius={[4, 4, 0, 0]} 
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}